"use client";

import { useTranslations } from "next-intl";
import { type ReactNode, useState } from "react";
import { Card, CardBody } from "@heroui/card";
import { FaPhoneVolume } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";

import { card } from "@/components/primitives";
import { siteConfig } from "@/config/site";

const URL_PATTERN = /https?:\/\/[^\s]+/g;

// Trailing sentence punctuation and an unmatched ")" end the sentence, not the
// URL: "Product overview: https://mcp.faxify.com." used to link to
// "https://mcp.faxify.com." (a URL Semrush could not crawl).
function trimUrl(url: string): string {
  let trimmed = url;

  for (;;) {
    const opens = (trimmed.match(/\(/g) ?? []).length;
    const closes = (trimmed.match(/\)/g) ?? []).length;

    if (/[.,;:!?]$/.test(trimmed)) {
      trimmed = trimmed.slice(0, -1);
    } else if (trimmed.endsWith(")") && closes > opens) {
      trimmed = trimmed.slice(0, -1);
    } else {
      return trimmed;
    }
  }
}

function linkify(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0;
    const url = trimUrl(match[0]);

    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      <a
        key={start}
        className="text-primary hover:text-primary/80 underline"
        href={url}
        rel="noopener noreferrer"
        target="_blank"
      >
        {url}
      </a>
    );
    cursor = start + url.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return nodes;
}

export interface FAQItem {
  key: string;
  question: string;
  answer: string;
}

export interface FAQContentProps {
  faqItems: FAQItem[];
  showContactSection?: boolean;
}

export default function FAQContent({
  faqItems,
  showContactSection: _showContactSection = true,
}: FAQContentProps) {
  const t = useTranslations("FAQ");
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (key: string) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);

      if (newSet.has(key)) {
        // If clicking on an open item, close it
        newSet.delete(key);
      } else {
        // If clicking on a closed item, close all others and open this one
        newSet.clear();
        newSet.add(key);
      }

      return newSet;
    });
  };

  return (
    <div>
      {/* FAQ Items */}
      <div className="space-y-4">
        {faqItems.map(item => (
          <div
            key={item.key}
            className="bg-content2 rounded-2xl border border-divider overflow-hidden"
          >
            <button
              aria-controls={`faq-answer-${item.key}`}
              aria-expanded={openItems.has(item.key)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-content3 transition-colors"
              onClick={() => toggleItem(item.key)}
            >
              <h2 className="text-lg font-semibold text-foreground pr-4">
                {item.question}
              </h2>
              <div className="flex-shrink-0">
                <svg
                  className={`w-5 h-5 text-default-500 transition-transform ${
                    openItems.has(item.key) ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M19 9l-7 7-7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </div>
            </button>
            <div
              className="px-6 pt-6 pb-4 bg-content1"
              hidden={!openItems.has(item.key)}
              id={`faq-answer-${item.key}`}
            >
              <div className="text-default-600 leading-relaxed">
                {item.answer.split("\n").map((line, index) => {
                  // Check if line starts with bullet point
                  if (line.trim().startsWith("•")) {
                    // Remove the bullet point from the text since we'll add it as a separate element
                    const textWithoutBullet = line.trim().substring(1).trim();

                    return (
                      <div key={index} className="flex items-center mb-2">
                        <span className="text-primary mr-2">•</span>
                        <div className="flex-1">
                          {linkify(textWithoutBullet)}
                        </div>
                      </div>
                    );
                  }

                  // Regular line (not a bullet point)
                  return (
                    <div key={index} className="mb-2">
                      {linkify(line)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Contact Section */}
      <div className="mt-16 text-center">
        <Card className={card()}>
          <CardBody>
            <h3 className="text-2xl font-bold text-foreground mb-4 text-center">
              {t("stillHaveQuestions.title")}
            </h3>
            <p className="text-default-600 mb-6 text-center">
              {t("stillHaveQuestions.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors"
                href={siteConfig.company.emailLink}
              >
                <MdOutlineEmail className="mr-2" size={16} />
                {siteConfig.company.email}
              </a>
              <a
                className="inline-flex items-center justify-center px-6 py-3 border border-default-300 text-foreground rounded-full font-medium hover:bg-default-100 transition-colors"
                href={siteConfig.company.phoneLink}
              >
                <FaPhoneVolume className="mr-2" size={16} />
                {siteConfig.company.phone}
              </a>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
